import { Inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { InvoiceService, TaxRateService } from '@galaxy/billing';
// eslint-disable-next-line @nx/enforce-module-boundaries
import {
  addNextUrl,
  AppOptionsKeys,
  ConversationDetail,
  ConversationMessage,
  ConversationTitleInfo,
  getSubjectParticipantByType,
  HostAppInterface,
  KabobDynamicButton,
  ParticipantService,
  SendMessage,
  SubjectParticipant,
} from '@galaxy/conversation/core';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { toFirestoreId } from '@galaxy/conversation/core/lib/conversation-utils';
import { FeatureFlagService, PartnerAccountGroupService } from '@galaxy/partner';
import { PaymentLinkItem } from '@vendasta/billing';
import {
  ConversationChannel,
  GlobalParticipantType,
  Participant,
  ParticipantType,
  PlatformLocation,
} from '@vendasta/conversation';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { newPartnerServiceContext, SSOService } from '@vendasta/sso';
import { Owner, UrlShortenerService } from '@vendasta/url-shortener';
import { catchError, firstValueFrom, forkJoin, map, mergeMap, Observable, of, switchMap, take } from 'rxjs';

@Injectable()
export class ConversationPartnerCenterService implements HostAppInterface {
  private partnerId = toSignal(this.partnerId$);

  constructor(
    private router: Router,
    private featureFlagService: FeatureFlagService,
    private snackbarService: SnackbarService,
    private participantService: ParticipantService,
    private invoiceService: InvoiceService,
    private ssoService: SSOService,
    private urlShortenerService: UrlShortenerService,
    private analyticsService: ProductAnalyticsService,
    private taxRateService: TaxRateService,
    private partnerAccountGroupService: PartnerAccountGroupService,
    @Inject('PARTNER_ID') private partnerId$: Observable<string>,
  ) {}

  getConversationTitleInfo(conversationDetail: ConversationDetail): ConversationTitleInfo {
    const recipients = this.getRecipient(conversationDetail);
    const conversationTitleInfo = {} as ConversationTitleInfo;
    const title: string[] = [];
    const secondarySubtitle: string[] = [];
    let subtitle = '';

    recipients.map((recipient) => {
      if (
        recipient.participantType == ParticipantType.PARTICIPANT_TYPE_CUSTOMER &&
        recipient.isParticipantInternalInfoDeleted
      ) {
        title.push('INBOX.ERROR.DELETED_CUSTOMER');
      } else {
        title.push(recipient.name || recipient.internalParticipantId);
        secondarySubtitle.push(recipient.internalParticipantId);
      }
    });
    conversationTitleInfo.title = title.filter((item) => item).join(', ');
    conversationTitleInfo.secondarySubtitle = secondarySubtitle.filter((item) => item).join(', ');

    // only returns a subtitle when there's only an accountGroup as recipient
    if (recipients.length === 1) {
      const address = recipients.find(
        (recipient) => recipient.participantType === ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
      )?.address;
      if (address) {
        subtitle = [address.firstLineAddress, address.secondLineAddress, address.city, address.state, address.country]
          .filter((item) => item)
          .join(', ');
      }
    }
    conversationTitleInfo.subtitle = subtitle;
    // Don't show the subtitle for openAI channel conversations
    if (conversationDetail.conversation.channel === ConversationChannel.CONVERSATION_CHANNEL_OPENAI) {
      conversationTitleInfo.subtitle = '';
      conversationTitleInfo.secondarySubtitle = '';
    }
    return conversationTitleInfo;
  }

  getRecipient(conversationDetail: ConversationDetail): Participant[] | null {
    const subjectParticipants = conversationDetail?.participants?.filter(
      (participant) => participant.isSubjectParticipant,
    );

    let recipientResult = subjectParticipants.filter(
      (recipient) => recipient.internalParticipantId !== this.partnerId(),
    );

    if (!recipientResult?.length) {
      recipientResult = subjectParticipants.map((subjectParticipant) => {
        let result = subjectParticipant;
        if (subjectParticipant.participantType !== ParticipantType.PARTICIPANT_TYPE_PARTNER) {
          result = new Participant({
            internalParticipantId: result.internalParticipantId,
            participantType: ParticipantType.PARTICIPANT_TYPE_PARTNER,
            isSubjectParticipant: true,
          });
        }
        return result;
      });
    }

    return recipientResult;
  }

  isSenderFromOrganization(message: ConversationMessage): boolean {
    if (
      message.sender?.participantType === ParticipantType.PARTICIPANT_TYPE_IAM_USER ||
      message.sender?.participantType === ParticipantType.PARTICIPANT_TYPE_PARTNER
    ) {
      return message.sender?.partnerId === this.partnerId() && !message.sender?.accountGroupId;
    }
    return (
      message.sender?.participantType === ParticipantType.PARTICIPANT_TYPE_DIGITAL_AGENT ||
      (message.sender?.participantType === ParticipantType.PARTICIPANT_TYPE_OPENAI_BOT &&
        message.channel === ConversationChannel.CONVERSATION_CHANNEL_WEB_CHAT)
    );
  }

  isYourExpert(): boolean {
    return false;
  }

  // this method only returns isInfoDeleted when the there's only an accountGroup as recipient
  isRecipientInternalInfoDeleted(conversationDetail: ConversationDetail): boolean {
    const recipient = this.getRecipient(conversationDetail) || [];
    if (recipient.length == 1) {
      return recipient.find(
        (subjectParticipant) => subjectParticipant?.participantType === ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
      )?.isParticipantInternalInfoDeleted;
    }
    return false;
  }

  private buildSubjectParticipant(
    partnerId: string,
    subjectParticipant: SubjectParticipant,
    channel: ConversationChannel,
  ): Participant {
    switch (subjectParticipant?.participantType) {
      case GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP:
        return this.participantService.buildAccountGroup(partnerId, subjectParticipant?.internalParticipantId, channel);
      case GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_PARTNER:
        return this.participantService.buildPartner(subjectParticipant?.internalParticipantId, channel);
      case GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_VENDOR:
        return this.participantService.buildVendor(subjectParticipant?.internalParticipantId, channel);
      default:
        return {} as Participant;
    }
  }

  buildParticipant(
    partnerId: string,
    recipient: SubjectParticipant,
    channel: ConversationChannel,
  ): Observable<Participant> {
    if (
      partnerId === 'VMF' &&
      recipient?.participantType === GlobalParticipantType.GLOBAL_PARTICIPANT_TYPE_ACCOUNT_GROUP
    ) {
      return this.partnerAccountGroupService.getPartnerAccountGroupMapping('', recipient?.internalParticipantId).pipe(
        catchError((err) => {
          console.error(err);
          if (err.status === 404) {
            this.snackbarService.openErrorSnack('Account is not associated with a Partner');
            throw new Error('Account is not associated with a Partner');
          }
          this.snackbarService.openErrorSnack('An error occurred, please try again');
          throw err;
        }),
        switchMap((partnerFromAccountGroup) => {
          return this.featureFlagService.batchGetStatus(partnerFromAccountGroup.partnerId, '', ['inbox_tab_pcc']).pipe(
            map((featureFlagsStatus) => ({
              featureFlags: featureFlagsStatus,
              partnerFromAccountGroup: partnerFromAccountGroup,
            })),
          );
        }),
        map((result) => {
          if (result.featureFlags['inbox_tab_pcc']) {
            return this.participantService.buildPartner(result.partnerFromAccountGroup.partnerId, channel);
          }
          this.snackbarService.openErrorSnack('Partner does not have the "inbox_tab_pcc" feature flag enabled');
          throw new Error('Partner does not have the "inbox_tab_pcc" feature flag enabled');
        }),
      );
    }
    return of(this.buildSubjectParticipant(partnerId, recipient, channel));
  }

  buildParticipants(currentIAMParticipant: Participant, participants: SubjectParticipant[]): Observable<Participant[]> {
    const channel = currentIAMParticipant?.channel;
    const participantList$: Observable<Participant>[] = [];

    participants.forEach((participant) => {
      participantList$.push(this.buildParticipant(currentIAMParticipant?.partnerId, participant, channel));
    });
    return forkJoin(participantList$).pipe(
      map((participants) => {
        participants.push(currentIAMParticipant);
        return participants;
      }),
    );
  }

  buildSendMessageParams(
    currentIAMParticipant: Participant,
    subjectParticipants: SubjectParticipant[],
  ): Observable<SendMessage> {
    return this.buildParticipants(currentIAMParticipant, subjectParticipants).pipe(
      map((participants) => ({
        participants: participants,
        location: PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER,
        channel: currentIAMParticipant?.channel,
      })),
    );
  }

  redirectToInternalConversation(
    currentIAMParticipant: Participant,
    recipientSubjectParticipant: SubjectParticipant[],
  ): void {
    this.buildSendMessageParams(currentIAMParticipant, recipientSubjectParticipant)
      .pipe(take(1))
      .subscribe((params) => this.router.navigate(['/inbox/send-message/'], { state: params }));
  }

  createPaymentLink(conversationDetail: ConversationDetail, paymentLinkItems: PaymentLinkItem[]): Observable<string> {
    const senderInternalId = getSubjectParticipantByType(
      conversationDetail.participants,
      ParticipantType.PARTICIPANT_TYPE_PARTNER,
    )?.internalParticipantId;
    const participantInternalId = getSubjectParticipantByType(
      conversationDetail.participants,
      ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
    )?.internalParticipantId;
    const trackingProps = {
      id: toFirestoreId(conversationDetail.conversation.conversationId),
      accountGroupId: participantInternalId,
      partnerId: senderInternalId,
      location: PlatformLocation.PLATFORM_LOCATION_PARTNER_CENTER,
      channel: conversationDetail.conversation.channel,
    };
    return this.invoiceService.createPaymentLink(senderInternalId, participantInternalId, paymentLinkItems).pipe(
      mergeMap((invoiceId) => {
        if (!invoiceId) {
          return of('');
        }
        this.analyticsService.trackEvent('inbox', 'conversation', 'create-invoice-success', 0, {
          ...trackingProps,
          invoiceId: invoiceId,
        });
        return this.ssoService.getEntryUrl('VBC', newPartnerServiceContext(senderInternalId), {
          path: `/invoice/${senderInternalId}/${invoiceId}`,
          skipGatewayAuthentication: true,
        });
      }),
      catchError((_) => {
        this.analyticsService.trackEvent('inbox', 'conversation', 'create-invoice-error', 0, trackingProps);
        return of('');
      }),
    );
  }

  shortenLink(link: string, accountGroupId = ''): Observable<string> {
    link = !link.match(/^http/) ? 'https://' + link : link;
    // TODO: Add new owner type in urlshortener service
    return this.urlShortenerService.createLink(accountGroupId, link, Owner.OWNER_UNTRACKED, '', false).pipe(
      catchError((_) => of(null)),
      map((result) => {
        if (!result) {
          return link;
        }
        return `https://${result.domain}${result.shortCode}`;
      }),
    );
  }

  calculatePaymentLinkTax(conversationDetail: ConversationDetail, amount: number): Observable<number> {
    const merchantId = getSubjectParticipantByType(
      conversationDetail.participants,
      ParticipantType.PARTICIPANT_TYPE_PARTNER,
    )?.internalParticipantId;
    const customerId = getSubjectParticipantByType(
      conversationDetail.participants,
      ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
    )?.internalParticipantId;
    return this.taxRateService.calculate(merchantId, customerId, amount).pipe(
      map((response) => {
        return response?.taxableAmount || 0;
      }),
      catchError((_) => {
        return of(0);
      }),
    );
  }

  getKabobAvailableActions(conversationDetail: ConversationDetail): Observable<KabobDynamicButton[]> {
    const accountGroupId = getSubjectParticipantByType(
      conversationDetail?.participants,
      ParticipantType.PARTICIPANT_TYPE_ACCOUNT_GROUP,
    )?.internalParticipantId;
    const actionsAvailable: KabobDynamicButton[] = [];
    if (accountGroupId) {
      const isRecipientDeleted = this.isRecipientInternalInfoDeleted(conversationDetail);
      if (!isRecipientDeleted) {
        const goToAccountAction: KabobDynamicButton = {
          title: 'INBOX.SENDER.GO_TO_ACCOUNT',
          action: () =>
            this.router
              .navigate([`/businesses/accounts/${accountGroupId}/details`])
              .then(() => this.router.navigate(['', { outlets: { inbox: null } }])),
          posthogLabel: 'clicked-go-to-account',
        };
        actionsAvailable.push(goToAccountAction);

        const openBusinessAppButton: KabobDynamicButton = {
          title: 'INBOX.SENDER.OPEN_BUSINESS_APP',
          action: () => this.openBusinessApp(accountGroupId),
          posthogLabel: 'clicked-open-business-app',
        };
        actionsAvailable.push(openBusinessAppButton);
      }
    }
    return of(actionsAvailable);
  }

  getAppOptions(): { [key in AppOptionsKeys]?: boolean } {
    return {};
  }

  openBusinessApp(accountGroupId: string): void {
    const entryUrl$ = this.ssoService.getEntryUrl('VBC', newPartnerServiceContext(this.partnerId()));
    firstValueFrom(entryUrl$).then((entryUrl) => {
      if (entryUrl) {
        window.open(addNextUrl(entryUrl, `/account/location/${accountGroupId}/dashboard`), '_blank');
      }
    });
  }
}
